/* eslint-disable tailwindcss/no-custom-classname */
import Head from "next/head";
import Link from "next/link";
import Image from "next/image";
import { Tab } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import ReCAPTCHA from "react-google-recaptcha";
// templates
import Meta from "@/components/templates/Meta";
import Layout from "@/components/templates/Layout";
import { Header } from "@/components/templates/Header";
import { Footer } from "@/components/templates/Footer";
// hooks
import { useBudouX } from "@/hooks/useBudouX";
import { CONSULTATION_TYPES } from "@/utils/contactValidation";
import { loadRecaptchaV3Script, executeRecaptchaV3 } from "@/utils/recaptchaUtils";
// molecules
import { Container } from "@/components/molecules/Container";
import { Inner } from "@/components/molecules/Inner";
import { Button, ButtonLink, TLink } from "@/components/molecules/Button";
import {
  TextField,
  TextAreaField,
  SelectField,
  CheckField,
  RadioField,
} from "@/components/molecules/Form";
// organisms
import Careers from "@/components/organisms/Careers";
// icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFaceSmileRelaxed } from "@fortawesome/pro-thin-svg-icons";
// Images
import leadContact from "/public/lead-contact.svg";
import leadContactH from "/public/lead-contactH.svg";
import coverContact from "/public/cover-contact.webp";
const textBody = [
  {
    head: "販売事業者名",
    body: "株式会社トッカシステムズ",
  },
  {
    head: "運営責任者名",
    body: "代表取締役　谷 樹",
  },
];
const textBodyAdd = [
  {
    head: "商品引き渡し時期",
    body: "制作初期費用のお支払いを確認後制作開始、およそ3週間後納品",
  },
  {
    head: "返品・交換不良品について",
    body: "商品の特性上、返金・キャンセルについてはお受けできません。",
  },
  {
    head: "解約手続きについて",
    body: "当初1年は解約は不可とします。1年以降は、30日前までの書面又はメールでの事前通知により解約可能とします。事前通知が30日未満の場合、当月分の管理費用のお支払いをお願いいたします。解約の届け出を確認後、利用サービスの契約終了をもって、契約の解約といたします。また、既に支払いいただいた料金の返金は致しかねます。",
  },
];

export default function ContactPage(props) {
  const { parse } = useBudouX();
  const router = useRouter();
  const isHeaderRelative = true;
  
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    tel: '',
    mail: '',
    subject: '',
    contact_content: ''
  });
  const [errors, setErrors] = useState({});
  const [isAgreed, setIsAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRecaptchaV2, setShowRecaptchaV2] = useState(false);
  const [recaptchaV2Token, setRecaptchaV2Token] = useState(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const recaptchaV2Ref = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      let recaptchaToken = null;
      
      if (showRecaptchaV2) {
        if (!recaptchaV2Token) {
          setErrors({ general: 'reCAPTCHAの認証を完了してください。' });
          setIsSubmitting(false);
          return;
        }
        recaptchaToken = recaptchaV2Token;
      } else {
        if (!recaptchaLoaded) {
          setErrors({ general: 'セキュリティ認証の準備中です。しばらくお待ちください。' });
          setIsSubmitting(false);
          return;
        }
        
        try {
          recaptchaToken = await executeRecaptchaV3(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, 'contact_form');
        } catch (error) {
          console.error('reCAPTCHA v3 execution failed:', error);
          setErrors({ general: 'セキュリティ認証に失敗しました。ページを再読み込みしてお試しください。' });
          setIsSubmitting(false);
          return;
        }
      }

      const response = await fetch('/api/contact/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken,
          recaptchaType: showRecaptchaV2 ? 'v2' : 'v3'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors) {
          setErrors(data.errors);
        }
        if (data.needsV2Fallback && !showRecaptchaV2) {
          setShowRecaptchaV2(true);
          setErrors({ general: '追加のセキュリティ認証が必要です。下記のチェックボックスにチェックを入れてください。' });
        }
      } else {
        sessionStorage.setItem('contactFormData', JSON.stringify(formData));
        sessionStorage.setItem('contactFormToken', data.token);
        sessionStorage.setItem('contactFormRecaptchaToken', recaptchaToken);
        sessionStorage.setItem('contactFormRecaptchaType', showRecaptchaV2 ? 'v2' : 'v3');
        router.push('/contact/confirm');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ general: 'フォームの送信中にエラーが発生しました。' });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const savedFormData = sessionStorage.getItem('contactFormData');
    if (savedFormData) {
      try {
        const parsedData = JSON.parse(savedFormData);
        setFormData(parsedData);
        const savedAgreement = sessionStorage.getItem('contactFormAgreement');
        if (savedAgreement === 'true') {
          setIsAgreed(true);
        }
      } catch (error) {
        console.error('Failed to restore form data:', error);
      }
    }

    const initRecaptcha = async () => {
      try {
        await loadRecaptchaV3Script(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY);
        setRecaptchaLoaded(true);
      } catch (error) {
        console.error('Failed to load reCAPTCHA:', error);
        setErrors({ general: 'セキュリティ認証の読み込みに失敗しました。ページを再読み込みしてお試しください。' });
      }
    };

    initRecaptcha();
  }, []);

  useEffect(() => {
    const checkbox = document.querySelector('input[name="confirmTerm_check"]');
    const button = document.querySelector('.contact-form-button');
    
    if (checkbox && button) {
      const updateButtonColor = () => {
        if (isAgreed) {
          button.style.backgroundColor = '#0073bf';
          button.style.borderColor = '#0073bf';
        } else {
          button.style.backgroundColor = '';
          button.style.borderColor = '';
        }
      };
      
      checkbox.addEventListener('change', updateButtonColor);
      updateButtonColor();
      
      return () => checkbox.removeEventListener('change', updateButtonColor);
    }
  }, [isAgreed]);

  useEffect(() => {
    const initButtonColorSync = () => {
      const checkbox = document.querySelector('input[name="confirmTerm_check"]');
      const button = document.querySelector('.contact-form-button');
      
      if (checkbox && button) {
        const syncButtonColor = () => {
          if (checkbox.checked) {
            button.style.backgroundColor = '#0073bf';
            button.style.borderColor = '#0073bf';
          } else {
            button.style.backgroundColor = '';
            button.style.borderColor = '';
          }
        };
        
        syncButtonColor();
        
        const observer = new MutationObserver(() => {
          syncButtonColor();
        });
        
        observer.observe(checkbox, { attributes: true, attributeFilter: ['checked'] });
        
        checkbox.addEventListener('change', syncButtonColor);
        
        return () => {
          observer.disconnect();
          checkbox.removeEventListener('change', syncButtonColor);
        };
      }
    };
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initButtonColorSync);
    } else {
      initButtonColorSync();
    }
  }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.textContent = `
      (function() {
        function initContactFormButtonSync() {
          const checkbox = document.querySelector('input[name="confirmTerm_check"]');
          const button = document.querySelector('.contact-form-button');
          
          if (checkbox && button) {
            function updateButtonColor() {
              if (checkbox.checked) {
                button.style.backgroundColor = '#0073bf';
                button.style.borderColor = '#0073bf';
              } else {
                button.style.backgroundColor = '';
                button.style.borderColor = '';
              }
            }
            
            updateButtonColor();
            
            checkbox.addEventListener('change', updateButtonColor);
            
            const observer = new MutationObserver(updateButtonColor);
            observer.observe(checkbox, { attributes: true, attributeFilter: ['checked'] });
            
            setInterval(updateButtonColor, 100);
          }
        }
        
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', initContactFormButtonSync);
        } else {
          initContactFormButtonSync();
        }
        
        setTimeout(initContactFormButtonSync, 0);
        setTimeout(initContactFormButtonSync, 100);
        setTimeout(initContactFormButtonSync, 500);
      })();
    `;
    document.head.appendChild(script);
    
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);
  return (
    <>
      <Meta
        pageTitle={"お問い合わせ"}
        pageDescription={"トッカシステムズへの【お問い合わせ】ページ"}
        pageImg={"https://tocca.systems/cover-contact.webp"}
        pagePath={"https://tocca.systems/contact/"}
        noIndex={true}
      />
      <Layout>
        <Header isRelative={isHeaderRelative} />
        <main className="flex-1">
          <Container>
            <Inner className="relative flex items-center justify-between gap-10 pt-10 pb-8 text-pri px-6 sm:px-8 md:px-12 md:py-10 lg:py-14 xl:px-0">
              <div className="space-y-3">
                <h2 className="block max-w-[285px] md:max-w-fit">
                  <span className="hidden md:block">
                    <Image
                      src={leadContactH}
                      alt="Get in touch with ToccaSystems"
                      quality={80}
                    />
                  </span>
                  <span className="block md:hidden">
                    <Image
                      src={leadContact}
                      alt="Get in touch with ToccaSystems"
                      quality={80}
                    />
                  </span>
                </h2>
                <div className="text-base font-bold text-ter md:text-md">
                  {parse(
                    "システム構築のご相談やご依頼など、お悩みのことがあれば、お気軽にお問い合わせください。"
                  )}
                </div>
              </div>
              <h4 className="hidden text-sec md:block md:text-6xl lg:text-7xl">
                <FontAwesomeIcon icon={faFaceSmileRelaxed} />
              </h4>
            </Inner>
            <div className="relative aspect-2/1 w-full">
              <Image
                src={coverContact}
                alt=""
                quality={80}
                layout="fill"
                objectFit="cover"
              />
            </div>
            <Inner className="grid grid-cols-1 gap-6 pt-8 pb-12 px-6 sm:px-8 md:px-12 md:grid-cols-4 md:gap-10 md:pt-14 md:pb-20 xl:px-0">
              <div className="order-2 col-span-1 md:order-1  md:col-span-3">
                <form onSubmit={handleSubmit} className="space-y-3 md:space-y-6">
                  <TextField
                    label="お名前"
                    id="name"
                    name="name"
                    type="text"
                    placeHolder=""
                    autoComplete="given-name"
                    value={formData.name}
                    onChange={handleInputChange}
                    error={errors.name}
                    required
                  />
                  <TextField
                    label="会社名"
                    id="company"
                    name="company"
                    type="text"
                    placeHolder=""
                    autoComplete="given-company"
                    value={formData.company}
                    onChange={handleInputChange}
                    error={errors.company}
                    required
                  />
                  <TextField
                    label="電話番号"
                    id="tel"
                    name="tel"
                    type="text"
                    placeHolder="日中ご連絡の付きやすい番号を入力してください"
                    autoComplete="given-tel"
                    value={formData.tel}
                    onChange={handleInputChange}
                    error={errors.tel}
                    required
                  />
                  <TextField
                    label="メールアドレス"
                    id="mail"
                    name="mail"
                    type="email"
                    placeHolder=""
                    autoComplete="given-mail"
                    value={formData.mail}
                    onChange={handleInputChange}
                    error={errors.mail}
                    required
                  />
                  <SelectField
                    className="col-span-full"
                    label="ご相談内容"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    error={errors.subject}
                  >
                    <option value="">
                      以下の項目より選択してください
                    </option>
                    {CONSULTATION_TYPES.map((type) => (
                      <option key={type.code} value={type.code}>
                        {type.label}
                      </option>
                    ))}
                  </SelectField>
                  <TextAreaField
                    label="お問い合わせ内容"
                    id="contact_content"
                    name="contact_content"
                    placeHolder="プロジェクトのご依頼の場合は、プロジェクトの概要・希望実施時期などをご記入ください。"
                    value={formData.contact_content}
                    onChange={handleInputChange}
                    error={errors.contact_content}
                    required
                  />
                  <div className="space-y-2">
                    <p className="text-sm text-txt-light">
                      ※ご入力いただいた情報は弊社、
                      <TLink href="/policy" variant="mono" color="pp">
                        個人情報保護方針
                      </TLink>
                      に則り管理いたします
                    </p>
                    <CheckField
                      className="gap-3"
                      label="個人情報保護方針に同意する"
                      id="confirmTerm_check"
                      name="confirmTerm_check"
                      checked={isAgreed}
                      onChange={(e) => {
                        setIsAgreed(e.target.checked);
                        sessionStorage.setItem('contactFormAgreement', e.target.checked.toString());
                      }}
                    />
                  </div>
                  {showRecaptchaV2 && (
                    <div className="space-y-2">
                      <p className="text-sm text-txt-light">
                        セキュリティ認証のため、下記のチェックボックスにチェックを入れてください。
                      </p>
                      <div className="flex justify-center">
                        <ReCAPTCHA
                          ref={recaptchaV2Ref}
                          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY}
                          onChange={(token) => {
                            setRecaptchaV2Token(token);
                            if (errors.general) {
                              setErrors({});
                            }
                          }}
                          onExpired={() => {
                            setRecaptchaV2Token(null);
                          }}
                          onError={() => {
                            setRecaptchaV2Token(null);
                            setErrors({ general: 'reCAPTCHA認証でエラーが発生しました。ページを再読み込みしてお試しください。' });
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {errors.general && (
                    <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                      {errors.general}
                    </div>
                  )}
                  <Button
                    type="submit"
                    variant="solid"
                    color="ter"
                    className={`h-16 w-full rounded-md md:h-24 contact-form-button ${
                      !isAgreed || isSubmitting || (showRecaptchaV2 && !recaptchaV2Token)
                        ? 'opacity-50 cursor-not-allowed' 
                        : ''
                    }`}
                    disabled={!isAgreed || isSubmitting || (showRecaptchaV2 && !recaptchaV2Token)}
                  >
                    {isSubmitting ? '送信中...' : '送信内容を確認する'}
                  </Button>
                  <style jsx>{`
                    form:has(input[name="confirmTerm_check"]:checked) .contact-form-button {
                      background-color: #0073bf !important;
                      border-color: #0073bf !important;
                    }
                  `}</style>
                </form>
              </div>
              <div className="order-1 col-span-1 md:order-2">
                <h3 className="mb-2 text-md font-bold text-pri md:text-base">
                  About Inquiries
                </h3>
                <div className="text-base text-txt-base md:text-sm">
                  ToccaSystemsへのプロジェクト案件のご相談や、各種ご依頼、その他のお問い合わせは、こちらのフォームからご連絡ください。担当者からご返信を差し上げます。採用に関する情報は
                  <TLink href="/careers/" variant="light" color="pp">
                    こちら
                  </TLink>
                  をご覧ください。
                </div>
              </div>
            </Inner>
          </Container>
        </main>
        <Careers />
        <Footer />
      </Layout>
    </>
  );
}
